/**
 * CheckoutSuccessPage
 * Landing spot after Stripe Checkout. Access is granted only once Stripe's
 * webhook writes the subscription doc, which can lag the redirect by a few
 * seconds — so we wait for it here instead of bouncing the new customer
 * back to the billing page.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '@/services/auth.service';
import { useRedirectWhenSubscribed } from '@/hooks/useRedirectWhenSubscribed';
import './styles/CheckoutSuccessPage.css';

const ACTIVATION_TIMEOUT_MS = 30_000;

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Navigates to /booth the moment the webhook writes an active subscription
  useRedirectWhenSubscribed();

  useEffect(() => {
    if (!getCurrentUser()) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), ACTIVATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [attempt]);

  const handleCheckAgain = () => {
    // The subscription listener is still live — just re-show the spinner
    // and re-arm the timeout instead of reloading the whole app.
    setTimedOut(false);
    setAttempt((n) => n + 1);
  };

  return (
    <div className="checkout-success-page">
      <div className="checkout-success-card">
        {!timedOut ? (
          <>
            <div className="checkout-success-spinner" aria-label="Loading" />
            <h1 className="checkout-success-title">You’re all set! 🎉</h1>
            <p className="checkout-success-text">
              Payment received — we’re activating your free trial. This usually
              takes just a few seconds.
            </p>
          </>
        ) : (
          <>
            <h1 className="checkout-success-title">Almost there…</h1>
            <p className="checkout-success-text">
              Your payment went through, but activation is taking longer than
              expected. Your account may take a minute to catch up.
            </p>
            <button
              className="checkout-success-button"
              onClick={handleCheckAgain}
            >
              Check Again
            </button>
            <p className="checkout-success-support">
              Still stuck? Email{' '}
              <a href="mailto:hello@sundaybestapp.com">
                hello@sundaybestapp.com
              </a>{' '}
              and we’ll get you going right away.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
